import { useCallback, useEffect, useState } from 'react';
import { useDataMode } from '@/contexts/DataModeContext';
import { useOptionalPodcastPlayer } from '@/contexts/PodcastPlayerContext';
import { PodcastDock } from '../../podcast/PodcastDock';
import { SectionAudioStrip } from '../../podcast/SectionAudioStrip';

/**
 * The Text reader's podcast: the strip under the section's title, and the
 * dock that stands in for it once it is off screen.
 *
 * `section` is the section on screen (1 for a lesson read as one page, null
 * on the contents list - no strip there). While the reader is open the
 * Navbar pill steps aside for this lesson (`readingLesson`): the strip and
 * dock show it here, the pill everywhere else.
 */
export function useTextPodcast({
  lessonId,
  section,
  openSection,
}: {
  lessonId: string | undefined;
  section: number | null;
  /** Open another section (the dock leads back to the one playing). */
  openSection: (index: number) => void;
}) {
  const { isApi } = useDataMode();
  const player = useOptionalPodcastPlayer();
  const [strip, setStrip] = useState<HTMLDivElement | null>(null);
  const stripOnScreen = useOnScreen(strip);
  const setReading = player?.setReadingLesson;

  useEffect(() => {
    if (!isApi || !lessonId || !setReading) return;
    setReading(lessonId);
    return () => setReading(null);
  }, [isApi, lessonId, setReading]);

  const track = player?.track;
  const ours = !!track && track.lessonId === lessonId;
  const showDock =
    isApi && ours && (track.sectionIndex !== section || !stripOnScreen);

  const onShow = useCallback(() => {
    if (!track) return;
    if (track.sectionIndex === section && strip) {
      strip.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      openSection(track.sectionIndex);
    }
  }, [track, section, strip, openSection]);

  return {
    strip:
      isApi && lessonId && section !== null ? (
        <SectionAudioStrip
          ref={setStrip}
          lessonId={lessonId}
          sectionIndex={section}
        />
      ) : null,
    dock: showDock ? <PodcastDock onShow={onShow} /> : null,
  };
}

/** The nearest ancestor set to scroll its content, or the document. By its
 *  style, not by whether it overflows yet: the text may still be loading. */
function scrollRoot(el: HTMLElement): Element | Document {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return document;
}

/**
 * Whether `el` is at least partly on screen, judged against the box that
 * scrolls it (the reader on a desktop, the page on a phone): measured when it
 * appears and on every scroll or resize. Measured rather than observed - an
 * IntersectionObserver reports nothing while the browser throttles a frame
 * (a story, an embed), which left the strip "off screen" in plain view.
 */
function useOnScreen(el: HTMLElement | null) {
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    if (!el) {
      setOnScreen(false);
      return;
    }
    const root = scrollRoot(el);
    const check = () => {
      const box = el.getBoundingClientRect();
      const view =
        root instanceof Element
          ? root.getBoundingClientRect()
          : { top: 0, bottom: window.innerHeight };
      setOnScreen(box.bottom > view.top && box.top < view.bottom);
    };
    check();
    const scroller = root instanceof Element ? root : window;
    scroller.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      scroller.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [el]);
  return onScreen;
}
